/**
 * Time, in one place.
 *
 * The business is in Somerset West and every wedding it shoots is in South
 * African time. Two kinds of value get confused in systems like this, so they
 * are kept apart deliberately:
 *
 *   A CALENDAR DAY — the wedding date, an invoice due date, a gallery expiry.
 *     Stored as a Postgres `date` and carried in TypeScript as `IsoDate`, a
 *     'YYYY-MM-DD' string. These have no timezone because they are not
 *     instants. "They marry on 14 March" is true in Auckland and in Lima.
 *
 *   AN INSTANT — when an email actually sends, when a quote was viewed, when a
 *     contract was signed. Stored as `timestamptz` and carried as a JS `Date`.
 *     Always UTC on the wire, always rendered in Africa/Johannesburg.
 *
 * The brief asks that dates be stored as timestamptz. That is right for
 * instants and wrong for calendar days: forcing a wedding date through an
 * instant means picking a moment on that day, and every subsequent read has to
 * guess which one. Postgres `date` is not a naive local string — it is a
 * first-class type with no timezone to get wrong — so calendar days use it, and
 * the scheduler converts to a real instant at the one point where that matters
 * (see `atTimeInZone`).
 *
 * Africa/Johannesburg is UTC+2 year round with no daylight saving, which makes
 * all of this simpler than it would be almost anywhere else. The code does not
 * rely on that; Intl does the work, so the app stays correct if it ever changes.
 */

import { TZDate } from '@date-fns/tz'
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns'

export const TIMEZONE = 'Africa/Johannesburg'

/** A calendar day with no timezone: 'YYYY-MM-DD'. */
export type IsoDate = string & { readonly __brand?: 'IsoDate' }

/** A wall-clock time with no date: 'HH:mm'. */
export type IsoTime = string & { readonly __brand?: 'IsoTime' }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const ISO_TIME = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/

export function isIsoDate(value: unknown): value is IsoDate {
  return typeof value === 'string' && ISO_DATE.test(value)
}

export function isIsoTime(value: unknown): value is IsoTime {
  return typeof value === 'string' && ISO_TIME.test(value)
}

// ---------------------------------------------------------------------------
// Calendar days
// ---------------------------------------------------------------------------

/** Today in Johannesburg, not today wherever the laptop happens to be. */
export function today(): IsoDate {
  return format(new TZDate(Date.now(), TIMEZONE), 'yyyy-MM-dd')
}

/** The calendar day an instant fell on, in Johannesburg. */
export function toIsoDate(instant: Date | string): IsoDate {
  const date = typeof instant === 'string' ? parseISO(instant) : instant
  return format(new TZDate(date, TIMEZONE), 'yyyy-MM-dd')
}

/**
 * Whole days from today until a calendar day, counted in Johannesburg.
 * Negative once the day has passed. This is the number on the pipeline card, so
 * it must not drift by one at 02:00 local time — which is exactly what
 * `Math.floor((then - now) / 86400000)` does.
 */
export function daysUntil(date: IsoDate, from: IsoDate = today()): number {
  return differenceInCalendarDays(parseISO(date), parseISO(from))
}

export function shiftDays(date: IsoDate, days: number): IsoDate {
  return format(addDays(parseISO(date), days), 'yyyy-MM-dd')
}

/** JS day of week for a calendar day: 0 Sunday … 6 Saturday. */
export function dayOfWeek(date: IsoDate): number {
  return parseISO(date).getDay()
}

/** 1–12. Used by the peak-season pricing rule. */
export function monthOf(date: IsoDate): number {
  return parseISO(date).getMonth() + 1
}

/** Monday to Thursday, which is what the weekday discount rule means. */
export function isWeekdayBooking(date: IsoDate): boolean {
  const day = dayOfWeek(date)
  return day >= 1 && day <= 4
}

// ---------------------------------------------------------------------------
// Instants
// ---------------------------------------------------------------------------

/**
 * The single bridge from calendar day to instant: 'this date, at this wall-clock
 * time, in Johannesburg'. Everything that schedules a send goes through here.
 *
 *   atTimeInZone('2027-03-14', '09:00')  →  2027-03-14T07:00:00.000Z
 *
 * Returns a plain `Date`, deliberately. A TZDate would carry its zone into
 * toISOString() and serialise as '+00:00' rather than 'Z' — harmless to
 * Postgres, but it makes the return value something subtly other than the Date
 * the signature promises, and that difference would surface somewhere far from
 * here.
 */
export function atTimeInZone(date: IsoDate, time: IsoTime = '09:00'): Date {
  const [hour = 0, minute = 0] = time.split(':').map(Number)
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number)
  return new Date(new TZDate(year, month - 1, day, hour, minute, 0, 0, TIMEZONE).getTime())
}

// ---------------------------------------------------------------------------
// Display
//
// One formatter per intent, so a date never renders two ways in two screens.
// Sentence case throughout, per the brief.
// ---------------------------------------------------------------------------

/** 14 March 2027 — the wedding date, wherever it is shown in full. */
export function formatDateLong(date: IsoDate): string {
  return format(parseISO(date), 'd MMMM yyyy')
}

/** Sat 14 Mar 2027 — lists and cards, where the weekday earns its space. */
export function formatDateShort(date: IsoDate): string {
  return format(parseISO(date), 'EEE d MMM yyyy')
}

/** 14 Mar — inside a dense row where the year is already established. */
export function formatDateTerse(date: IsoDate): string {
  return format(parseISO(date), 'd MMM')
}

/** 14 Mar 2027, 09:00 — an instant, rendered in Johannesburg. */
export function formatInstant(instant: Date | string): string {
  const date = typeof instant === 'string' ? parseISO(instant) : instant
  return format(new TZDate(date, TIMEZONE), 'd MMM yyyy, HH:mm')
}

/** 14 Mar 2027 — the day an instant fell on, in Johannesburg ("Sent 14 Mar 2027"). */
export function formatDayOf(instant: Date | string): string {
  const date = typeof instant === 'string' ? parseISO(instant) : instant
  return format(new TZDate(date, TIMEZONE), 'd MMM yyyy')
}

/** 09:00 — a wall-clock time, trimmed of seconds. */
export function formatTime(time: IsoTime): string {
  return time.slice(0, 5)
}

/**
 * 'in 34 days', 'tomorrow', 'today', '6 days ago'.
 * Sentence fragments, not sentences, so they drop into a row without a full stop.
 */
export function formatDaysUntil(date: IsoDate, from: IsoDate = today()): string {
  const days = daysUntil(date, from)

  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  if (days > 0) return `in ${days} days`
  return `${Math.abs(days)} days ago`
}

/** '2 days ago', '3 hours ago', 'just now' — for received_at, sent_at and friends. */
export function formatAgo(instant: Date | string): string {
  const date = typeof instant === 'string' ? parseISO(instant) : instant
  const seconds = Math.round((Date.now() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'

  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`

  const days = differenceInCalendarDays(new TZDate(Date.now(), TIMEZONE), new TZDate(date, TIMEZONE))
  if (days < 30) return `${days} ${days === 1 ? 'day' : 'days'} ago`

  return formatDateShort(toIsoDate(date))
}
